import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ApiError, legalDocumentsApi } from '@/lib/pillarApi';
import { LegalAgreementImpactConfirmation } from './LegalAgreementImpactConfirmation';

interface RemoveOrganizationAssignmentButtonProps {
    readonly organizationUuid: string;
    readonly legalDocumentAssignmentUuid: string;
    /** The document's title and version, e.g. "Terms (v2)". */
    readonly documentLabel: string;
}

/**
 * The server's own explanation, never the transport fallback, which can carry
 * a raw HTTP status or response body (BUG-261).
 */
function removalErrorMessage(error: unknown): string {
    const serverMessage = error instanceof ApiError ? error.responseMessage : undefined;
    return serverMessage === undefined
        ? 'The assignment could not be removed. Try again.'
        : `The assignment could not be removed: ${serverMessage}`;
}

/**
 * Removes one agreement assignment from an Organization's Legal card: it
 * revokes the assignment after the usual impact confirmation.
 */
export function RemoveOrganizationAssignmentButton({
    organizationUuid,
    legalDocumentAssignmentUuid,
    documentLabel,
}: RemoveOrganizationAssignmentButtonProps) {
    const queryClient = useQueryClient();
    const [confirming, setConfirming] = useState(false);
    const [removalError, setRemovalError] = useState<string | null>(null);
    const removeMutation = useMutation({
        mutationFn: async () =>
            await legalDocumentsApi.revokeLegalDocumentAssignment(legalDocumentAssignmentUuid),
        onSuccess: async () => {
            setConfirming(false);
            setRemovalError(null);
            await queryClient.invalidateQueries({
                queryKey: ['organizationAgreements', organizationUuid],
            });
        },
        onError: (error: unknown) => {
            setConfirming(false);
            setRemovalError(removalErrorMessage(error));
        },
    });
    return (
        <>
            <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label={`Remove ${documentLabel} from this Organization`}
                title="Remove this assignment"
                disabled={removeMutation.isPending}
                onClick={() => {
                    setRemovalError(null);
                    setConfirming(true);
                }}
            >
                <X className="h-4 w-4" aria-hidden="true" />
            </Button>
            {removalError === null ? null : (
                <p role="alert" className="w-full text-sm text-red-700">
                    {removalError}
                </p>
            )}
            <LegalAgreementImpactConfirmation
                request={
                    confirming
                        ? {
                              action: 'revoke',
                              legal_document_assignment_uuid: legalDocumentAssignmentUuid,
                          }
                        : null
                }
                title={`Remove ${documentLabel} from this Organization?`}
                confirmLabel="Remove"
                isConfirming={removeMutation.isPending}
                confirmBlocked={(impact) => impact.leaves_no_platform_default}
                onConfirm={() => removeMutation.mutate()}
                onCancel={() => setConfirming(false)}
            />
        </>
    );
}
